/**
 * AppRail — the unified 64px icon-only shell per Home v1 spec §3.1.
 *
 * One shell for every route (was three: Layout/AppLayout/PublicLayout).
 * Auth-awareness lives inside the account menu, not at the layout level,
 * because the rail itself is identical for anonymous and authenticated
 * visitors — only the account affordance differs.
 *
 * Responsive: 64px fixed-left at ≥901px, 64px fixed-top horizontal scroll
 * strip at ≤900px per TOKENS.md §8.
 */

import React, { useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import { Outlet, Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import AltRouteIcon from "@mui/icons-material/AltRoute";
import HubIcon from "@mui/icons-material/Hub";
import TuneIcon from "@mui/icons-material/Tune";
import TerminalIcon from "@mui/icons-material/Terminal";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import DescriptionIcon from "@mui/icons-material/Description";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PeopleIcon from "@mui/icons-material/People";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import PsychologyIcon from "@mui/icons-material/Psychology";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import MenuIcon from "@mui/icons-material/Menu";

import { useTranslation } from "react-i18next";

import { PRODUCT_NAMES, PRODUCT_ORDER, ProductPath } from "../data/productOrder";
import { useAuth } from "../hooks/useAuth";
import { useThemeMode } from "../hooks/useThemeMode";
import LanguageSwitcher from "./LanguageSwitcher";
import RouteErrorBoundary from "./RouteErrorBoundary";
import Footer from "./Footer";

const RAIL_SIZE = 64;

interface Product {
  path: string;
  /** i18n key (common namespace) for the display label — resolved with t() at render. */
  labelKey?: string;
  /** Canonical public name when product discovery surfaces must share one label. */
  label?: string;
  Icon: typeof AltRouteIcon;
  // Prefix patterns that should mark this product as the active rail item.
  // The BIAE aggregate (AI Interview Coach) covers many sub-tool routes.
  matches: string[];
}

type ProductMeta = Omit<Product, "path">;

// Display metadata per product. When adding a product: add its path to
// PRODUCT_ORDER in ../data/productOrder.ts AND its metadata entry here
// (the Record<ProductPath, …> type makes tsc fail if you forget this one).
// Labels are i18n keys, not literal text — resolved via t() where rendered
// (AppRail.product.* in common.json).
const PRODUCT_META: Record<ProductPath, ProductMeta> = {
  "/career-transition": {
    labelKey: "AppRail.product.careerTransition",
    Icon: AltRouteIcon,
    matches: ["/career-transition"],
  },
  "/agentic-ai-mastery": {
    labelKey: "AppRail.product.agenticAiMastery",
    Icon: HubIcon,
    matches: ["/agentic-ai-mastery"],
  },
  "/agentic-ops": {
    labelKey: "AppRail.product.agenticOps",
    Icon: TuneIcon,
    matches: ["/agentic-ops"],
  },
  "/claude-harness": {
    labelKey: "AppRail.product.claudeHarness",
    Icon: TerminalIcon,
    matches: ["/claude-harness"],
  },
  "/portfolio-kit": {
    labelKey: "AppRail.product.portfolioKit",
    Icon: FolderSpecialIcon,
    matches: ["/portfolio-kit"],
  },
  "/resume-mastery": {
    labelKey: "AppRail.product.resumeMastery",
    Icon: DescriptionIcon,
    matches: ["/resume-mastery"],
  },
  "/agentic-interviewing": {
    label: PRODUCT_NAMES.agenticAiInterviewing,
    Icon: MenuBookIcon,
    matches: ["/agentic-interviewing"],
  },
  "/linkedin-presence": {
    labelKey: "AppRail.product.linkedinPresence",
    Icon: PeopleIcon,
    matches: ["/linkedin-presence"],
  },
  "/forge-coach": {
    labelKey: "AppRail.product.forgeCoach",
    Icon: PsychologyIcon,
    matches: ["/forge-coach"],
  },
  "/coach": {
    labelKey: "AppRail.product.aiInterviewCoach",
    Icon: RecordVoiceOverIcon,
    matches: [
      "/coach",
      "/evaluate",
      "/evaluations",
      "/mock",
      "/generator",
      "/questions",
      "/templates",
      "/coaching",
      "/analytics",
      "/moderation",
      "/answers",
    ],
  },
  "/visibility-strategy": {
    labelKey: "AppRail.product.visibilityWorkshop",
    Icon: VisibilityIcon,
    matches: ["/visibility-strategy"],
  },
};

// Order is projected from the canonical PRODUCT_ORDER — single source of truth.
export const PRODUCTS: Product[] = PRODUCT_ORDER.map((path) => ({
  path,
  ...PRODUCT_META[path],
}));

function matchesActive(pathname: string, prefixes: string[]): boolean {
  return prefixes.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
}

export default function AppRail() {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [productMenuAnchor, setProductMenuAnchor] = useState<HTMLElement | null>(null);

  const activePath = useMemo(() => {
    const hit = PRODUCTS.find((p) => matchesActive(location.pathname, p.matches));
    return hit?.path ?? null;
  }, [location.pathname]);

  const handleAccountClick = (e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(e.currentTarget);
  };
  const closeMenu = () => setMenuAnchor(null);
  const closeProductMenu = () => setProductMenuAnchor(null);
  const productLabel = (product: Product) =>
    product.label ?? t(product.labelKey ?? product.path);
  const handleLogout = () => {
    closeMenu();
    logout();
    navigate("/");
  };
  const handleBilling = () => {
    closeMenu();
    navigate("/pricing");
  };
  const handleCoachDashboard = () => {
    closeMenu();
    navigate("/coaching");
  };
  const handleFounderMetrics = () => {
    closeMenu();
    navigate("/admin/metrics");
  };
  const handleLogin = () => {
    closeMenu();
    navigate("/login");
  };
  const handleThemeToggle = () => {
    toggleMode();
    // Menu stays open intentionally — users often toggle & compare.
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <Box
        component="nav"
        aria-label={t("AppRail.primaryNav")}
        sx={{
          position: "fixed",
          zIndex: theme.zIndex.appBar,
          bgcolor: "background.paper",
          borderColor: "divider",
          display: "flex",
          ...(isMobile
            ? {
                top: 0,
                left: 0,
                right: 0,
                height: RAIL_SIZE,
                flexDirection: "row",
                overflow: "hidden",
                borderBottom: 1,
                px: 1,
                gap: 0.5,
                alignItems: "center",
              }
            : {
                top: 0,
                bottom: 0,
                left: 0,
                width: RAIL_SIZE,
                flexDirection: "column",
                borderRight: 1,
                py: 1.5,
                gap: 0.5,
                alignItems: "center",
              }),
        }}
      >
        <Tooltip title={t("AppRail.homeTooltip")} placement={isMobile ? "bottom" : "right"}>
          <IconButton
            component={RouterLink}
            to="/"
            aria-label={t("AppRail.homeTooltip")}
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              padding: 0,
              overflow: "hidden",
              flexShrink: 0,
              "&:hover": { opacity: 0.85 },
            }}
          >
            <Box
              component="img"
              src="/logo192.png"
              alt=""
              sx={{ width: 36, height: 36, display: "block" }}
            />
          </IconButton>
        </Tooltip>

        {!isMobile && <Divider flexItem sx={{ mx: 1, my: 0.5 }} />}

        {isMobile ? (
          <>
            <Button
              color="inherit"
              startIcon={<MenuIcon />}
              onClick={(event) => setProductMenuAnchor(event.currentTarget)}
              aria-haspopup="menu"
              aria-controls={productMenuAnchor ? "product-navigation-menu" : undefined}
              aria-expanded={Boolean(productMenuAnchor)}
              sx={{
                minHeight: 44,
                px: 1,
                color: "text.secondary",
                fontWeight: 500,
                textTransform: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                "&:hover": { color: "text.primary", bgcolor: "action.hover" },
              }}
            >
              {t("AppRail.productsMenu")}
            </Button>
            <Menu
              id="product-navigation-menu"
              anchorEl={productMenuAnchor}
              open={Boolean(productMenuAnchor)}
              onClose={closeProductMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{ paper: { sx: { minWidth: 260, maxHeight: "calc(100vh - 76px)" } } }}
            >
              {PRODUCTS.map((product) => {
                const isActive = activePath === product.path;
                const { Icon } = product;
                const label = productLabel(product);
                return (
                  <MenuItem
                    key={product.path}
                    selected={isActive}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => {
                      closeProductMenu();
                      navigate(product.path);
                    }}
                    sx={{ minHeight: 44, gap: 1.5 }}
                  >
                    <Icon fontSize="small" />
                    {label}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              flex: 1,
              overflowY: "auto",
            }}
          >
            {PRODUCTS.map((product) => {
              const isActive = activePath === product.path;
              const { Icon } = product;
              const label = productLabel(product);
              return (
                <Tooltip key={product.path} title={label} placement="right">
                  <IconButton
                    component={RouterLink}
                    to={product.path}
                    aria-label={label}
                    aria-current={isActive ? "page" : undefined}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      color: isActive ? "secondary.main" : "text.secondary",
                      bgcolor: isActive
                        ? alpha(theme.palette.secondary.main, 0.08)
                        : "transparent",
                      transition: theme.transitions.create(["background-color", "color"], {
                        duration: theme.transitions.duration.shortest,
                      }),
                      "&:hover": {
                        color: isActive ? "secondary.main" : "text.primary",
                        bgcolor: isActive
                          ? alpha(theme.palette.secondary.main, 0.12)
                          : "action.hover",
                      },
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>
        )}

        {!isMobile && <Divider flexItem sx={{ mx: 1, my: 0.5 }} />}

        {user?.is_admin && (
          <Tooltip
            title={t("AppRail.founderMetrics")}
            placement={isMobile ? "bottom" : "right"}
          >
            <IconButton
              component={RouterLink}
              to="/admin/metrics"
              aria-label={t("AppRail.founderMetrics")}
              aria-current={
                matchesActive(location.pathname, ["/admin/metrics"])
                  ? "page"
                  : undefined
              }
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                color: matchesActive(location.pathname, ["/admin/metrics"])
                  ? "secondary.main"
                  : "text.secondary",
                bgcolor: matchesActive(location.pathname, ["/admin/metrics"])
                  ? alpha(theme.palette.secondary.main, 0.08)
                  : "transparent",
                flexShrink: 0,
                "&:hover": { color: "text.primary", bgcolor: "action.hover" },
              }}
            >
              <QueryStatsIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}

        <LanguageSwitcher placement={isMobile ? "bottom" : "right"} />

        <Tooltip
          title={isAuthenticated ? t("AppRail.account") : t("AppRail.signIn")}
          placement={isMobile ? "bottom" : "right"}
        >
          <IconButton
            onClick={handleAccountClick}
            aria-label={isAuthenticated ? t("AppRail.accountMenu") : t("AppRail.signIn")}
            aria-haspopup="menu"
            sx={{
              width: 40,
              height: 40,
              color: "text.secondary",
              flexShrink: 0,
              "&:hover": { color: "text.primary", bgcolor: "action.hover" },
            }}
          >
            <PersonIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{
            vertical: isMobile ? "bottom" : "center",
            horizontal: isMobile ? "right" : "right",
          }}
          transformOrigin={{
            vertical: isMobile ? "top" : "center",
            horizontal: isMobile ? "right" : "left",
          }}
          slotProps={{ paper: { sx: { minWidth: 200, ml: isMobile ? 0 : 1 } } }}
        >
          <MenuItem onClick={handleThemeToggle}>
            {mode === "dark" ? (
              <LightModeIcon fontSize="small" sx={{ mr: 1.5 }} />
            ) : (
              <DarkModeIcon fontSize="small" sx={{ mr: 1.5 }} />
            )}
            {mode === "dark" ? t("AppRail.lightMode") : t("AppRail.darkMode")}
          </MenuItem>
          {isAuthenticated ? (
            [
              <Divider key="div" />,
              ...(user?.is_coach
                ? [
                    <MenuItem key="coach-dashboard" onClick={handleCoachDashboard}>
                      <SupervisorAccountIcon fontSize="small" sx={{ mr: 1.5 }} />
                      {t("AppRail.coachDashboard")}
                    </MenuItem>,
                  ]
                : []),
              ...(user?.is_admin
                ? [
                    <MenuItem key="founder-metrics" onClick={handleFounderMetrics}>
                      <QueryStatsIcon fontSize="small" sx={{ mr: 1.5 }} />
                      {t("AppRail.founderMetrics")}
                    </MenuItem>,
                  ]
                : []),
              <MenuItem key="billing" onClick={handleBilling}>
                <CreditCardIcon fontSize="small" sx={{ mr: 1.5 }} />
                {t("AppRail.billing")}
              </MenuItem>,
              <MenuItem key="logout" onClick={handleLogout}>
                <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
                {t("AppRail.logOut")}
              </MenuItem>,
            ]
          ) : (
            <MenuItem onClick={handleLogin}>
              <LoginIcon fontSize="small" sx={{ mr: 1.5 }} />
              {t("AppRail.signIn")}
            </MenuItem>
          )}
        </Menu>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          ...(isMobile ? { pt: `${RAIL_SIZE}px` } : { pl: `${RAIL_SIZE}px` }),
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <RouteErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </RouteErrorBoundary>
        </Box>
        <Footer />
      </Box>
    </Box>
  );
}
